import { useContext, useEffect, useMemo, useState } from "react";
import { BoxFull, Card } from "iuvity-ui-components";
import { AppContext } from "../../context/index";
import { formatDateTime } from "../../helpers/formatDate";
import { ClientList } from "../shared/ClientList";
import { SettingsUsed } from "../shared/SettingsUsed";
import { LastChangesTable } from "../shared/LastChangesTable";
import { TextsAndMessages } from "../shared/TextsAndMessages";
import { DigitalChannelSettings } from "../shared/DigitalChannelSettings";
import { useAppInfo } from "../../hooks/useAppInfo";
import { useQuickLinksConsole } from "../../hooks/useQuickLinksConsole";
import { useAudit } from "../../hooks/useAudit";
import jp from "jsonpath";

export const DashMenu = () => {
  const {
    appState: { language, user, ip, reloadQuickLinks, isLoading, infoObjRolesByTenantState: {
      dataRolesByTenantGlobal,
      functionalitiesCodesMap,
    }},
    t,
  } = useContext(AppContext);

  // Obtengo los permisos para la funcionalidad de Textos Y Mensajes
  const textAndMsgPrivileges = (!!dataRolesByTenantGlobal && !!functionalitiesCodesMap)
    && jp.query(dataRolesByTenantGlobal, `$..[?(@.functionalityId=="${functionalitiesCodesMap['/translations']}")].permission.privilegeId`);

  const lastAccessDate = formatDateTime(t, language);
  const { data: dataAppInfo } = useAppInfo("DATA");
  const { data: dataQuickLinksAppDashboard, reloadFn } = useQuickLinksConsole("APP_DASHBOARD");
  const { data: dataQuickLinksTools } = useQuickLinksConsole("TOOLS_DASHBOARD");
  const { data: dataAudit } = useAudit("DATA");
  const [digitalChannelData, setDigitalChannelData] = useState([]);

  const columnsLastChanges = useMemo(() => {
    if (language) {
      return [
        {
          field: "creationDate",
          header: t("Dashboard.updatedAtLabel"),
        },
        {
          field: "functionality",
          header: t("Dashboard.functionalityLabel"),
        },
        {
          field: "userEmail",
          header: t("Dashboard.userLabel"),
        },
        {
          field: "activity",
          header: t("Dashboard.activityLabel"),
        },
      ];
    } else {
      return [];
    }
  }, [t, language]);

  let textsAndMessagesData = [
    {
      iconname: `iu iu-icon-download`,
      name: t("Dashboard.downloadBtn"),
      linkto: `/translations`
    },
  ];
  
  textsAndMessagesData = (!!textAndMsgPrivileges && textAndMsgPrivileges?.includes('CREATE')) ? [
    {
      iconname: `iu iu-icon-plus`,
      name: t("Dashboard.createLabelBtn"),
      linkto: `/translations/new`
    },
    ...textsAndMessagesData
  ]
  : textsAndMessagesData;

  useEffect(() => {
    if (dataAppInfo) {
      //delete dataAppInfo.versionApp;
      const res: any = Object?.entries(dataAppInfo)?.map((item: any) => {
        return {
          name: t(`Dashboard.${item[0]}`),
          value: item[1]
        }
      });
      res.push(
        {
          iconname: "iu iu-icon-arrowsright",
          value: t('Dashboard.formatsAndLanguages')
        }
        );
        setDigitalChannelData(res);
      } else {
        const data: any = [
          {
            name: t("Dashboard.defaultCurrency"),
            value: "-"
          },
          {
            name: t("Dashboard.defaultLanguage"),
            value: "-"
          },
          {
            name: t("Dashboard.versionApp"),
            value: "-"
          },
          {
            iconname: "iu iu-icon-arrowsright",
            value: t("Dashboard.formatsAndLanguages")
          }
        ];
        setDigitalChannelData(data);
      }
  }, [dataAppInfo, t, language]);

  useEffect(() => {
    if (reloadQuickLinks) {
      reloadFn();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[reloadQuickLinks]);

  return (
    <div className="dashboard-internal-container">
      <BoxFull className="p-0" style={{ backgroundColor: "transparent" }}>
        <div className="dash-c-header">
          <div className="dash-c-header-welcome">
            <h1 className="title">{t("Dashboard.welcomeTitle")}</h1>
            <h1 className="subtitle">{user?.name} {user?.lastname}</h1>
          </div>
          <div className="dash-c-header-us-info">
             <p className="us-info-extra">
               {lastAccessDate}
             </p>
             <p className="us-info-extra">
               {ip}
             </p>
          </div>
        </div>

      </BoxFull>
      
      {(!isLoading && dataQuickLinksAppDashboard?.length > 0)
        ? 
          <ClientList 
            dataTools={dataQuickLinksAppDashboard} 
            title={t("Dashboard.mostUsedConfigurationsTitle")} 
          />
        :
          <div className="skeleton-menu-container">
            <div className="skeleton">
                {[1,2,3].map(() => (
                  <Card className="card-skeleton"></Card>
                ))}
            </div>
          </div>
      }

      {(!isLoading && dataQuickLinksAppDashboard?.length > 0) || (!isLoading && dataQuickLinksAppDashboard?.length === 0)
        ?
          <BoxFull className={`${dataQuickLinksAppDashboard?.length === 0 ? "mt-0 mb-5" : "my-5"} p-0`} style={{ backgroundColor: "transparent" }}>
            <div className={(dataQuickLinksTools?.length > 0 && !!textAndMsgPrivileges && textAndMsgPrivileges?.includes('READ'))
                ? `config-grid-dash-menu ${dataQuickLinksAppDashboard?.length === 0 ? "mt-0" : "mt-5"}` 
                : ((dataQuickLinksTools?.length === 0 && dataAudit?.length === 0) || (dataQuickLinksTools?.length > 0 && !!textAndMsgPrivileges && !textAndMsgPrivileges?.includes('READ')))
                  ? `config-grid-dash-menu-2 ${dataQuickLinksAppDashboard?.length === 0 ? "mt-0" : "mt-5"}` 
                  : `config-grid-dash-menu-3 ${dataQuickLinksAppDashboard?.length === 0 ? "mt-0" : "mt-5"}`}>
                <DigitalChannelSettings
                  title={t("Dashboard.digitalChannelConfigurationTitle")}
                  data={digitalChannelData}
                />
                {dataQuickLinksTools?.length > 0 && <SettingsUsed
                  title={t("Dashboard.toolsTitle")}
                  data={dataQuickLinksTools}
                />}
                {
                  (!!textAndMsgPrivileges && textAndMsgPrivileges?.includes('READ')) &&
                  <TextsAndMessages
                    title={t("Dashboard.textsAndMessagesTitle")}
                    data={textsAndMessagesData}
                  />
                }
                {dataAudit?.length > 0 && <LastChangesTable
                  title={t("Dashboard.latestChangesAppTitle")}
                  data={dataAudit}
                  columns={columnsLastChanges}
                />}
              </div>
          </BoxFull>
        :
          <div className="skeleton-widgets-container">
            <div className="card-1"></div>
            <div className="card-2"></div>
          </div>
      }
    </div>
  );
};
